import LimitFunctionTable from "@/components/LimitFunctionTable";
import {
  StrongPointItem,
  Button,
  PostProjectFlowItem,
  AnimeFadeIn,
  FadeIn,
} from "./";
import { FC } from "react";
import styled from "styled-components";
import { clsx } from "clsx";
import useDevice from "@/hooks/useDevice";
import Image from "next/image";
import DefaultLayout from "@/layouts/DefaultLayout";
import { NextSeo } from "next-seo";
import publicPath from "@/utils/publicPath";
import usePageView from "@/hooks/usePageView";
import useMultiImageLoader from "@/hooks/useMultiImageLoader";

const Wrapper = styled.div`
  font-family: "Helvetica Neue", "Tazugane Gothic", sans-serif;
  background-color: #fff;
  color: #333;
`;

const AboutPage: FC = () => {
  usePageView();
  const { isSp } = useDevice();

  // トップの画像を読み込む
  const { isComplete } = useMultiImageLoader([
    "/imgs/about/Header_Image03.webp",
    "/imgs/about/Header_Image04.webp",
    "/imgs/about/Header_Image01.webp",
    "/imgs/about/Header_Image02.webp",
  ]);

  return (
    <DefaultLayout>
      <NextSeo
        title="TECTUREについて"
        description="TECTURE（テクチャー）は、設計者（設計・インテリア）の「探す」と「発信」の2つの課題を解決するためのWebサービスです。"
        canonical={publicPath("/about")}
      />
      <Wrapper>
        <main className="overflow-hidden">
          <header
            className={clsx(
              "relative w-full",
              "aspect-[375/461]",
              "laptop:aspect-[1440/838] laptop:max-h-[838px] laptop:max-w-[1440px] laptop:mx-auto",
            )}
          >
            <div
              className={clsx(
                "absolute left-1/2 transorm -translate-x-1/2 flex flex-col items-center  text-center z-10 w-full",
                "top-[50%] -translate-y-1/2 px-16 gap-12",
                "laptop:top-[16%] laptop:translate-y-0 laptop:gap-24",
              )}
            >
              <p
                className={clsx(
                  "font-bold",
                  "text-[max(18px,3.2vw)]",
                  "laptop:text-[min(2.7vw,42px)]",
                )}
              >
                設計者のための検索プラットフォーム
              </p>
              <Image
                className={clsx("laptop:w-[40%] w-[min(80%,480px)]")}
                src="/imgs/about/Logo_default.svg"
                alt={"TECTURE"}
                width={574}
                height={77}
                priority={true}
                unoptimized={true}
              />
              <p
                className={clsx(
                  "font-bold text-center tracking-widest leading-loose",
                  "text-[max(15px,2.8vw)]",
                  "laptop:text-[min(2.4vw,34px)]",
                )}
              >
                設計者（設計・インテリア）の
                <br className="laptop:hidden" />
                <span className={clsx("border-b-4 border-yellow-500")}>
                  「探す」
                </span>
                と
                <span className={clsx("border-b-4 border-yellow-500")}>
                  「発信」
                </span>
                。
                <br />
                2つの課題を解決するためのWebサービスです。
              </p>
            </div>

            {isComplete && (
              <>
                <FadeIn delay={0.2}>
                  <div
                    className={clsx(
                      "absolute bg-[#F4F5F6] top-0",
                      "w-[14.6666667%] left-[8.2666667%] aspect-[55/120]",
                      "laptop:w-[13.8888889%] laptop:left-[4.0972222%] laptop:aspect-[200/550]",
                    )}
                  />
                </FadeIn>

                <FadeIn delay={0.4}>
                  <div
                    className={clsx(
                      "absolute bg-[#F4F5F6]",
                      "w-[34.6666667%] aspect-[130/60] bottom-[9.978308%] left-[5.3333333%]",
                      "laptop:bottom-[12.8878282%] laptop:left-[16.8055556%] laptop:aspect-[385/183] laptop:w-[26.7361111%]",
                    )}
                  />
                </FadeIn>

                <FadeIn delay={0.6}>
                  <div
                    className={clsx(
                      "absolute bottom-0 bg-[#F4F5F6]",
                      "right-[5.3333333%] aspect-[225/91] w-[60%]",
                      "laptop:right-[21.0416667%] laptop:aspect-[514/183] laptop:w-[35.6944444%]",
                    )}
                  />
                </FadeIn>

                <FadeIn delay={0.8}>
                  <div
                    className={clsx(
                      "absolute bg-[#F4F5F6]",
                      "top-0 right-0 aspect-[176/102] w-[46.9333333%]",
                      "laptop:top-[18.8544153%] laptop:right-[0%] laptop:aspect-[270/180] laptop:w-[18.75%]",
                    )}
                  />
                </FadeIn>

                <FadeIn delay={0}>
                  <div
                    className={clsx(
                      "absolute bg-cover bg-no-repeat top-0 left-0",
                      "w-[19.4666667%] aspect-[73/110]",
                      "laptop:w-[15.6944444%] laptop:aspect-[226/491]",
                    )}
                  >
                    <Image
                      src="/imgs/about/Header_Image01.webp"
                      alt=""
                      unoptimized={true}
                      fill
                    />
                  </div>
                </FadeIn>

                <FadeIn delay={0.4}>
                  <div
                    className={clsx(
                      "absolute left-0 bg-no-repeat bg-cover",
                      "bottom-[14.9333333%] aspect-[140/60] w-[37.3333333%]",
                      "laptop:bottom-[3.5799523%] laptop:aspect-[558/224] laptop:h-[26.7303103%]",
                    )}
                  >
                    <Image
                      src="/imgs/about/Header_Image02.webp"
                      alt=""
                      unoptimized={true}
                      priority={true}
                      fill
                    />
                  </div>
                </FadeIn>

                <FadeIn delay={0.6}>
                  <div
                    className={clsx(
                      "absolute right-0 bg-no-repeat bg-cover",
                      "bottom-[2.1691974%] aspect-[208/88] w-[55.4666667%]",
                      "laptop:bottom-[6.6825776%] laptop:aspect-[599/283] laptop:w-[41.5972222%]",
                    )}
                  >
                    <Image
                      src="/imgs/about/Header_Image04.webp"
                      alt=""
                      unoptimized={true}
                      priority={true}
                      fill
                    />
                  </div>
                </FadeIn>

                <FadeIn delay={0.2}>
                  <div
                    className={clsx(
                      "absolute top-0 right-0 bg-no-repeat bg-cover",
                      "aspect-[170/82] w-[45.3333333%]",
                      "laptop:aspect-[310/227] laptop:w-[21.5277778%]",
                    )}
                  >
                    <Image
                      src="/imgs/about/Header_Image03.webp"
                      alt=""
                      unoptimized={true}
                      priority={true}
                      fill
                    />
                  </div>
                </FadeIn>
              </>
            )}
          </header>

          <section
            className={clsx(
              "relative mt-[40px]",
              "laptop:mt-[240px]",
              "before:absolute before:transform `before:block before:content-[''] before:bg-[url(/imgs/about/SEARCH.svg)] before:bg-cotain before:bg-no-repeat before:opacity-30",
              "before:w-[316px] before:h-[74px] before:top-[-18px] before:left-1/2 before:-translate-x-[calc(50%-20px)]",
              "laptop:before:w-[911px] laptop:before:h-[215px] laptop:before:top-0 laptop:before:left-1/2 laptop:before:-translate-y-[calc(50%+20px)] laptop:before:-translate-x-[calc(50%-270px)]",
            )}
          >
            <div>
              <div
                className={clsx(
                  "flex justify-center gap-32 bg-[#FAFAFA] ",
                  "flex-col-reverse px-20 pt-[72px]",
                  "laptop:flex-row laptop:pt-40",
                )}
              >
                <div
                  className={clsx(
                    "flex justify-center",
                    "laptop:flex-col laptop:justify-end laptop:items-end",
                  )}
                >
                  <Image
                    src="/imgs/about/Image_illust_01.webp"
                    alt="仕事時間の半分が探すことに悩む建築家のイラスト"
                    width={354}
                    height={449}
                  />
                </div>

                <div
                  className={clsx(
                    "relative flex flex-col gap-24",
                    "items-center",
                    "laptop:items-start laptop:pb-[72px]",
                  )}
                >
                  <h2
                    className={clsx(
                      "relative font-bold",
                      "text-[24px] text-center",
                      "laptop:text-[45px] laptop:text-left",
                      "before:absolute before:top-[-32px] before:left-1/2 before:transform before:-translate-x-1/2 before:bg-black before:block before:content-[''] before:w-[76px] before:h-[5px]",
                      "laptop:before:-translate-x-0 laptop:before:left-0 laptop:before:top-[-72px]",
                    )}
                  >
                    設計者の業務時間のうち、
                    <br className="" />
                    半分を奪っている
                    <span
                      className={clsx(
                        "inline-block border-b-4 border-yellow-500",
                      )}
                    >
                      <span
                        className={clsx("text-[40px]", "laptop:text-[55px]")}
                      >
                        「探す」時間。
                      </span>
                    </span>
                  </h2>
                  <p
                    className={clsx(
                      "font-bold leading-loose",
                      "text-[16px]",
                      "laptop:text-[26px]",
                    )}
                  >
                    「あの照明良かったけど、どこのメーカーだったっけ？」
                    <br />
                    設計者はこんなことが日常茶飯事。
                    <br />
                    TECTUREは悩み・検索する時間をよりクリエイティブにします。
                  </p>
                </div>
              </div>

              <div
                className={clsx(
                  "space-y-64 ",
                  "px-16 py-80",
                  "laptop:px-64 laptop:py-120",
                )}
              >
                <ul
                  className={clsx(
                    "grid max-w-[1440px] mx-auto",
                    "gap-[40px]",
                    "laptop:grid-cols-3 laptop:gap-[24px]",
                  )}
                >
                  {[
                    {
                      title: "メーカー横断検索",
                      description:
                        "あらゆるメーカーの製品が登録されているため、メーカーをまたいだ比較・検討が簡単です。",
                      imgSrc: "/imgs/about/Image_point01_01.webp",
                      imgAlt:
                        "TECTUREに登録されている主要建材・家具メーカーのロゴ",
                    },
                    {
                      title: "詳細検索",
                      description:
                        "色・サイズ・素材などの項目で検索することができます。",
                      imgSrc: "/imgs/about/Image_point01_02.webp",
                      imgAlt:
                        "TECTUREで検索できる詳細項目の1部を抜粋して表示する",
                    },
                    {
                      title: "サンプル請求",
                      description:
                        "いくつものメーカーのウェブサイトを廻ることなく、TECTUREアカウント1つでサンプルを請求することができます。",
                      imgSrc: "/imgs/about/Image_point01_03.webp",
                      imgAlt: "現実で行うサンプル請求の大変さを表した写真",
                    },
                  ].map(({ title, description, imgSrc, imgAlt }, index) => (
                    <li
                      key={title}
                      style={{
                        paddingTop: isSp ? 0 : index * 90,
                      }}
                    >
                      <StrongPointItem
                        index={index + 1}
                        title={title}
                        description={description}
                        imgSrc={imgSrc}
                        imgAlt={imgAlt}
                      />
                    </li>
                  ))}
                </ul>

                <div className={clsx("flex justify-center mx-auto")}>
                  <Button
                    href={"/signup"}
                    label={"さあ使ってみよう!"}
                    aria-label={"アカウント登録ページ"}
                  />
                </div>
              </div>
            </div>
          </section>

          <section className={clsx("mt-80", "laptop:mt-[120px]")}>
            <section
              className={clsx(
                "relative before:absolute before:transform `before:block before:content-['']",
                "before:bg-[url(/imgs/about/BRANDING.svg)] before:bg-cotain before:bg-no-repeat before:opacity-30",
                "before:w-[328px] before:h-[59px] before:top-[-24px] before:left-1/2 before:-translate-x-[calc(50%-20px)]",
                "laptop:before:w-[1196px] laptop:before:h-[215px] laptop:before:top-0 laptop:before:left-1/2 laptop:before:-translate-y-[calc(50%+16px)] laptop:before:-translate-x-[calc(50%+84px)]",
                "before:z-10",
              )}
            >
              <div
                className={clsx(
                  "relative flex items-start justify-center bg-[#FAFAFA]",
                  "pt-[72px]",
                  "laptop:px-64 laptop:pb-[70px] laptop:pt-0",
                )}
              >
                <div
                  className={clsx(
                    "relative z-10 flex px-16 pb-24",
                    "flex-col items-center gap-24",
                    "laptop:flex-row laptop:pb-0 laptop:gap-0",
                  )}
                >
                  <div
                    className={clsx(
                      "relative flex flex-col gap-24",
                      "items-center",
                      "laptop:items-start",
                    )}
                  >
                    <h2
                      className={clsx(
                        "relative font-bold",
                        "text-[24px] text-center",
                        "laptop:text-[45px] laptop:text-left",
                        "before:absolute before:top-[-32px] before:left-1/2 before:transform before:-translate-x-1/2 before:bg-black before:block before:content-[''] before:w-[76px] before:h-[5px]",
                        "laptop:before:-translate-x-0 laptop:before:left-0 laptop:before:top-[-72px]",
                      )}
                    >
                      作品の奥深さや魅力を、
                      <br />
                      <span
                        className={clsx(
                          "border-b-4 border-yellow-500",
                          "text-[36px]",
                          "laptop:text-[55px]",
                        )}
                      >
                        もっと多くの人に伝えるために。
                      </span>
                    </h2>
                    <p
                      className={clsx(
                        "font-bold leading-relaxed max-w-[800px]",
                        "laptop:text-[26px]",
                      )}
                    >
                      TECTUREに作品を投稿すると、
                      <br />
                      あらゆるメディアに掲載されるチャンスが生まれます。
                    </p>
                  </div>
                  <Image
                    src="/imgs/about/Image_illust_02.webp"
                    width={440}
                    height={342}
                    alt="TECTUREに投稿することでたくさんの人の目に触れるチャンスが生まれるイラスト"
                  />
                </div>
              </div>

              <div
                className={clsx(
                  "space-y-64 ",
                  "px-16 py-80",
                  "laptop:px-64 laptop:py-120",
                )}
              >
                <ul
                  className={clsx(
                    "grid max-w-[1440px] mx-auto",
                    "gap-[40px]",
                    "laptop:grid-cols-3 laptop:gap-[24px]",
                  )}
                >
                  {[
                    {
                      title: "メディアでの発信",
                      description:
                        "投稿された作品は、TECTURE編集部によって選出し、 25万ユーザーのメディア「TECTURE MAG」に掲載します。 バイリンガルで発信するため、世界のメディアの目に触れる可能性も高まります。",
                      imgSrc: "/imgs/about/Image_point02_01.webp",
                      imgAlt:
                        "TECTUREが運営するメディア「TECTURE MAG」のPCおよびスマホでのイメージ",
                    },
                    {
                      title: "SNSでの発信",
                      description:
                        "TECTUREの公式SNSは合計10万のユーザーがフォロー。 英語での発信が中心で、海外に拡散されやすい運営をしています。",
                      imgSrc: "/imgs/about/Image_point02_02.webp",
                      imgAlt:
                        "TECTUREが利用している主要SNS(Instagram, Facebook, X)のアイコンが並んでいる",
                    },
                    {
                      title: "写真にピンをつけよう",
                      description:
                        "あなたのお気に入りの建材・家具を写真に紐づけましょう。 写真に情報を付加するほど、TECTURE内での閲覧頻度が高くなります。",
                      imgSrc: "/imgs/about/Image_point02_03.webp",
                      imgAlt:
                        "建築・インテリアにプロダクト製品のひもづいたピンがついている",
                    },
                  ].map(({ title, description, imgSrc, imgAlt }, index) => (
                    <li
                      key={title}
                      style={{
                        paddingTop: isSp ? 0 : index * 90,
                      }}
                    >
                      <StrongPointItem
                        index={index + 1}
                        title={title}
                        description={description}
                        imgSrc={imgSrc}
                        imgAlt={imgAlt}
                      />
                    </li>
                  ))}
                </ul>

                <div className={clsx("flex justify-center mx-auto")}>
                  <Button
                    href={"/signup"}
                    label={"さあ使ってみよう!"}
                    aria-label={"アカウント登録ページ"}
                  />
                </div>
              </div>
            </section>

            <section className={clsx("bg-[#fafafa] py-80 px-16")}>
              <div
                className={clsx(
                  "max-w-[1024px] mx-auto flex flex-col gap-[64px]",
                )}
              >
                <div className={clsx("flex flex-col items-center ")}>
                  <h2 className={clsx("font-bold text-[30px]")}>
                    作品投稿までの流れ
                  </h2>
                  <p
                    className={clsx(
                      "text-yellow font-bold text-[30px]",
                      "laptop:text-[40px]",
                    )}
                  >
                    INTRODUCTION
                  </p>
                </div>

                <ul className={clsx("grid gap-[120px]", "laptop:grid-cols-3")}>
                  {[
                    {
                      title: "PROアカウントをつくる",
                      description:
                        "まずはTECTUREでPROアカウントを作成します。作品投稿はPROアカウントの専用機能です。",
                      imgSrc: "/imgs/about/Image_step01.webp",
                      imgAlt: "設計者個人を表すアカウントのイラスト",
                    },
                    {
                      title: "チームを申請する",
                      description:
                        "作品を投稿するためにチームを作成しましょう。チームは〇〇設計事務所などの設計者のまとまりです。",
                      imgSrc: "/imgs/about/Image_step02.webp",
                      imgAlt: "設計者個人が集団になったチームを表すイラスト",
                    },
                    {
                      title: "プロジェクトを投稿する",
                      description:
                        "いよいよ作品の投稿です。作品の概要・仕上げ材・空間用途などを登録したら準備完了。作品を公開しましょう！",
                      imgSrc: "/imgs/about/Image_step03.webp",
                      imgAlt: "チームによって投稿される建築作品を表すイラスト",
                    },
                  ].map((item, index) => (
                    <li key={item.title}>
                      <AnimeFadeIn delay={index * 0.12}>
                        <PostProjectFlowItem {...item} index={index + 1} />
                      </AnimeFadeIn>
                    </li>
                  ))}
                </ul>
              </div>
            </section>
          </section>
        </main>

        <aside className={clsx("py-80 laptop:py-160 px-16")}>
          <div
            className={clsx(
              "flex flex-col gap-[40px] items-center max-w-[1024px] mx-auto",
            )}
          >
            <p className={clsx("text-center max-w-[700px] text-[18px]")}>
              TECTUREは設計者(設計・インテリア)の方に向けた専用の機能を用意しています。
              <br className="small:hidden" />
              PROアカウントをご登録いただくことで、専用の機能をご利用頂けます。
            </p>

            <LimitFunctionTable />

            <Button href={"/signup"} label={"アカウントの開設へ"} />
          </div>
        </aside>
      </Wrapper>
    </DefaultLayout>
  );
};
export default AboutPage;
